import { useRef, useState } from 'preact/hooks';
import { isValidEmail } from '@/email';
import { useEmailSuggestion } from '@/useEmailSuggestion';
import { CheckoutShell } from '@/components/CheckoutShell';
import { t, useT } from '@/i18n/t';

interface Props {
  // Prefills the email field — the passwordless-first login screen carries the
  // email the visitor just typed into the magic-link send, so a send failure
  // lands them on a pre-filled retry form instead of a blank one.
  initialEmail?: string;
  onSubmit: (email: string) => void;
  onBack: () => void;
  // Resets the sent confirmation back to the email form ("Use a different
  // email" on the sent screen) — the parent owns the sent state.
  onDifferentEmail: () => void;
  loading: boolean;
  sent: boolean;
  error: string | null;
  // Set on the standalone login (the publisher login entry point, no checkout
  // behind it): hide the 3-step indicator. Omitted mid-checkout, where the
  // steps are real and the in-body Back returns to the gate.
  standalone?: boolean;
  onDismiss?: () => void;
}

export function LoginStep({ initialEmail, onSubmit, onBack, onDifferentEmail, loading, sent, error, standalone = false, onDismiss }: Props) {
  useT();
  const [email, setEmail] = useState(initialEmail ?? '');
  // Client-side rejection of a malformed address (e.g. TLD-less), shown in
  // place of the parent's backend error so the visitor gets feedback without a
  // round trip. Cleared on edit and on a valid submit.
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError(t('emailField.invalid'));
      return;
    }
    setEmailError(null);
    onSubmit(trimmed);
  };

  // Non-blocking domain-typo hint ("gmail.con" → "Did you mean gmail.com?").
  // The submit path above still accepts the typed address — the hint only
  // offers a one-click fix. Revealed on blur, hidden on focus.
  const { suggestion: emailSuggestion, revealSuggestion, hideSuggestion } = useEmailSuggestion(email);
  const emailInputRef = useRef<HTMLInputElement>(null);

  return (
    // The email-for-link form reads identically wherever it appears — the same
    // title/subtitle/button as the passwordless login screen (EmailStep
    // intent="login"). `standalone` only drops the checkout step indicator.
    <CheckoutShell ariaLabel={t('emailStep.title.access')} currentStep={1} hideSteps={standalone} onDismiss={onDismiss}>
      <h2 class="sm-title">{t('emailStep.title.access')}</h2>
      <p class="sm-subtitle">{t('emailStep.subtitle.access')}</p>

      {sent ? (
        <>
          <div class="sm-success-panel" role="status">
            <strong>{t('loginStep.sent.title')}</strong>
            <p>
              {t('loginStep.sent.body')}
            </p>
          </div>
          {/* Recovery fallbacks for the email that never arrives. The publisher
              support line rides the shell (CheckoutShell renders it when the
              publisher configured one), so it is not repeated here. */}
          <p class="sm-sent-help">
            {t('loginStep.sent.help')} {t('loginStep.sent.spam')}
          </p>
          <div class="sm-magiclink-center">
            <button class="sm-secondary-link" type="button" onClick={onDifferentEmail}>
              {t('loginStep.sent.differentEmail')}
            </button>
          </div>
        </>
      ) : (
        <form class="sm-form" onSubmit={handleSubmit}>
          <label class="sm-field">
            <span>{t('emailField.label')}</span>
            <input
              ref={emailInputRef}
              type="email"
              class="sm-input"
              placeholder={t('emailField.placeholder')}
              value={email}
              onInput={(e) => {
                setEmail((e.target as HTMLInputElement).value);
                setEmailError(null);
              }}
              onBlur={revealSuggestion}
              onFocus={hideSuggestion}
              required
              autofocus
              disabled={loading}
              aria-label={t('emailField.label')}
            />
          </label>

          {emailSuggestion && (
            <p class="sm-hint">
              {t('emailField.didYouMean')}{' '}
              <button
                type="button"
                class="sm-hint-address"
                onClick={() => {
                  setEmail(emailSuggestion);
                  setEmailError(null);
                  emailInputRef.current?.focus();
                }}
              >
                {emailSuggestion}
              </button>
              ?
            </p>
          )}

          {(emailError ?? error) && <p class="sm-error" role="alert">{emailError ?? error}</p>}

          <button
            type="submit"
            class="sm-btn sm-btn-primary"
            disabled={loading || !email.trim().includes('@')}
          >
            {loading && <span class="sm-btn-spinner" aria-hidden="true" />}
            {loading ? t('loginStep.submitting') : t('emailStep.magicLink')}
          </button>
        </form>
      )}

      <button class="sm-btn sm-btn-secondary" type="button" onClick={onBack} disabled={loading}>
        {t('common.back')}
      </button>
    </CheckoutShell>
  );
}
